import { FileWarning } from "lucide-react";
import { useMemo } from "react";

import { useFileContent } from "~/hooks/use-file-content";
import { ThemedLoadingSpinner } from "~/theming/feature/themed-loading-spinner";

import { StatusCard } from "../status-card";
import { FileProperties } from "./file-properties";
import { JUnitReportView } from "./junit/junit-report-view";
import { JUnitTestSuite, parseJUnitXml } from "./junit/parse-junit-xml";
import { SelectionAction } from "./selection-action";
import { TextFileViewer } from "./text-file-viewer";
import { ViewerPrefs } from "./url-fragment";

interface XmlFileViewerProps {
  content: string | Uint8Array;
  properties: FileProperties;
  versionId?: string;
  initialView?: ViewerPrefs;
  lineRange?: { startLine: number; endLine: number } | null;
  onSelectionChange?: (action: SelectionAction) => void;
}

const JUNIT_ROOT_PATTERN = /^\s*(?:<\?xml[^?]*\?>\s*)?<(?:testsuites|testsuite)\b/;

type JUnitParseResult = { detected: false } | { detected: true; suites: JUnitTestSuite[] };

export function XmlFileViewer({ content, properties, ...rest }: XmlFileViewerProps) {
  const fileContentState = useFileContent(content);

  const junitResult = useMemo((): JUnitParseResult | null => {
    if (fileContentState.status !== "success") return null;
    if (!JUNIT_ROOT_PATTERN.test(fileContentState.value.slice(0, 500))) return { detected: false };
    try {
      return { detected: true, suites: parseJUnitXml(fileContentState.value) };
    } catch {
      return { detected: false };
    }
  }, [fileContentState]);

  if (fileContentState.status === "error") {
    return (
      <StatusCard
        icon={<FileWarning />}
        title="Failed to load file"
        description="Failed to load the file. Please try again later."
      />
    );
  }

  if (fileContentState.status === "loading" || junitResult == null) {
    return (
      <StatusCard
        icon={<ThemedLoadingSpinner width="24px" height="24px" />}
        title="Loading file"
        description="Preparing a preview..."
      />
    );
  }

  if (!junitResult.detected) {
    return <TextFileViewer content={content} properties={properties} {...rest} />;
  }

  return <JUnitReportView suites={junitResult.suites} />;
}
